import React, { useState, useEffect, useMemo } from 'react';
import { Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { getAPI, postAPI } from 'apis/backend/apiNode';
import Icon from 'components/icon/Icon';
import StockTransferModal from './StockTransferModal';

// -------- Styles --------
const S = {
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: '1.5rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '12px 10px',
    textAlign: 'left',
    color: '#64748b',
    borderBottom: '2px solid #f1f5f9',
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase',
  },
  td: {
    padding: '12px 10px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 13,
    color: '#1e293b',
    cursor: 'pointer',
  },
};

const SummaryCard = ({ title, value, color, subtext }) => (
  <div style={{
    background: '#fff',
    border: `1px solid ${color}22`,
    borderRadius: 12,
    padding: '12px 16px',
    flex: '1 1 180px',
    minWidth: 140,
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  }}>
    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    {subtext && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{subtext}</div>}
  </div>
);

const MaterialsTab = ({ intervention }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // -------- Fetch --------
  useEffect(() => {
    fetchMovements();
  }, [intervention]);

  const fetchMovements = async () => {
    if (!intervention?._id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getAPI('stock', `/movements?source=intervention&sourceId=${intervention._id}`);
      const data = response?.data || response || [];
      setMovements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching stock movements:', err);
      setError('Impossible de charger les mouvements de stock.');
    } finally {
      setLoading(false);
    }
  };

  // -------- Groupement --------
  const groupedData = useMemo(() => {
    const groups = {};
    movements.forEach(mov => {
      const productId = mov.produitId?._id;
      if (!productId) return;
      const entrepotId = mov.entrepotId?._id || null;
      const carId = mov.carId?._id || mov.carId || null;
      const key = `${productId}_${entrepotId || 'null'}_${carId || 'null'}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          product: mov.produitId,
          entrepotId,
          entrepotObj: mov.entrepotId || null,
          carId,
          carObj: mov.carId || null,
          movements: []
        };
      }
      groups[key].movements.push(mov);
    });
    Object.values(groups).forEach(group => {
      group.movements.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    });
    return Object.values(groups);
  }, [movements]);

  // -------- Helpers --------
  const getLocationLabel = (group) => {
    const { movements, entrepotObj } = group;
    const lastMov = movements[movements.length - 1];
    const lastType = lastMov?.type;

    if (lastType === 'sortie') return 'Chez client';
    if (lastMov?.carId) {
      const car = lastMov.carId;
      let label = 'Véhicule';
      if (car?.conducteurs?.length) {
        const tech = car.conducteurs[0]?.technicien;
        if (tech?.name) label = `Véhicule (${tech.name})`;
      } else if (car?.nom) {
        label = `Véhicule: ${car.nom}`;
      } else if (car?._id) {
        label = `Véhicule (ID: ${car._id})`;
      }
      return label;
    }
    return entrepotObj?.nom ? `Entrepôt: ${entrepotObj.nom}` : '—';
  };

  const getTypeLabel = (type) => ({
    entree: 'Entrée',
    sortie: 'Sortie',
    reservation: 'Réservation',
    liberation: 'Retour',
    ajustement: 'Ajustement'
  }[type] || type);

  const getTypeBadgeVariant = (type) => ({
    entree: 'success',
    sortie: 'danger',
    reservation: 'warning',
    liberation: 'info',
    ajustement: 'secondary'
  }[type] || 'light');

  const getProductName = (product) => product?.description || product?.name || 'Produit';

  // -------- Statistiques --------
  const stats = useMemo(() => {
    const totalProducts = groupedData.length;
    const totalQty = groupedData.reduce((sum, g) => {
      const last = g.movements[g.movements.length - 1];
      return sum + (last?.quantite || 0);
    }, 0);
    const reserved = groupedData.filter(g => g.movements.some(m => m.type === 'reservation')).length;
    const consumed = groupedData.filter(g => g.movements.some(m => m.type === 'sortie')).length;
    return { totalProducts, totalQty, reserved, consumed };
  }, [groupedData]);

  // -------- Actions --------
  const handleRemove = async (produitId, entrepotId, quantite, carId = null) => {
    if (!window.confirm('Voulez-vous vraiment libérer cette réservation ?')) return;
    try {
      await postAPI('stock', {
        produitId, entrepotId, quantite,
        source: 'intervention',
        sourceId: intervention?._id,
        notes: `intervention pose ${intervention.reference}`,
        carId,
      }, '/liberer');
      alert('Réservation libérée.');
      fetchMovements();
    } catch (err) {
      alert('Erreur lors de la libération.');
    }
  };

  const handleValidate = async (produitId, entrepotId, quantite, carId = null) => {
    try {
      await postAPI('stock', {
        produitId, entrepotId, quantite,
        source: 'intervention',
        sourceId: intervention?._id,
        notes: `intervention pose ${intervention.reference}`,
        carId,
      }, '/consommer');
      alert('Réservation consommée.');
      fetchMovements();
    } catch (err) {
      alert('Erreur lors de la consommation.');
    }
  };

  const toggleRow = (key) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  // -------- Chargement --------
  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Chargement des matériels...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* En‑tête */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          <Icon icon="Box" className="me-2" />
          Matériels utilisés
        </div>
        <Button size="sm" variant="primary" onClick={() => setShowTransferModal(true)}>
          <Icon icon="Plus" className="me-1" /> Ajouter
        </Button>
      </div>

      {error && <Alert variant="danger" className="py-1">{error}</Alert>}


      {/* Tableau */}
      <div style={S.card}>
        {groupedData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            Aucun matériel enregistré pour cette intervention.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: 30 }}></th>
                  <th style={S.th}>Produit</th>
                  <th style={S.th}>Qté</th>
                  <th style={S.th}>Emplacement</th>
                  <th style={S.th}>Statut</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedData.map((group) => {
                  const { key, product, entrepotId, carId, movements: prodMovements } = group;
                  const lastMov = prodMovements[prodMovements.length - 1];
                  const lastType = lastMov?.type;
                  const qty = lastMov?.quantite || 0;
                  const locationLabel = getLocationLabel(group);
                  const showActions = lastType === 'reservation';
                  const isExpanded = expandedRows.has(key);

                  return (
                    <React.Fragment key={key}>
                      <tr onClick={() => toggleRow(key)} style={{ cursor: 'pointer' }}>
                        <td style={S.td}>
                          <Icon icon={isExpanded ? 'ChevronDown' : 'ChevronRight'} />
                        </td>
                        <td style={S.td}><strong>{getProductName(product)}</strong></td>
                        <td style={S.td}>{qty}</td>
                        <td style={S.td}>{locationLabel}</td>
                        <td style={S.td}>
                          {lastType && (
                            <Badge bg={getTypeBadgeVariant(lastType)}>
                              {getTypeLabel(lastType)}
                            </Badge>
                          )}
                        </td>
                        <td style={S.td}>
                          {showActions && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                className="me-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleValidate(product._id, entrepotId, qty, carId);
                                }}
                                title="Consommer"
                              >
                                <Icon icon="Check" /> Sortie Stock
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemove(product._id, entrepotId, qty, carId);
                                }}
                                title="Libérer"
                              >
                                <Icon icon="Close" /> Retour Stock
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>

                      {/* Ligne étendue (historique) */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="6" style={{ padding: '0 10px 10px 10px', background: '#f8fafc' }}>
                            <div style={{ padding: '12px', background: '#f1f5f9', borderRadius: 6 }}>
                              <strong style={{ fontSize: 12, color: '#475569' }}>Historique des mouvements</strong>
                              <table style={{ ...S.table, marginTop: 8, background: 'white', borderRadius: 4, overflow: 'hidden' }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...S.th, fontSize: 10 }}>Type</th>
                                    <th style={{ ...S.th, fontSize: 10 }}>Qté</th>
                                    <th style={{ ...S.th, fontSize: 10 }}>Date</th>
                                    <th style={{ ...S.th, fontSize: 10 }}>Emplacement</th>
                                    <th style={{ ...S.th, fontSize: 10 }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {prodMovements.map((mov, i) => {
                                    const loc = mov.carId
                                      ? (mov.carId?.conducteurs?.[0]?.technicien?.name
                                        ? `Véhicule (${mov.carId.conducteurs[0].technicien.name})`
                                        : 'Véhicule')
                                      : (mov.entrepotId?.nom || '—');
                                    return (
                                      <tr key={mov._id || i}>
                                        <td style={{ ...S.td, fontSize: 12 }}>
                                          <Badge bg={getTypeBadgeVariant(mov.type)}>
                                            {getTypeLabel(mov.type)}
                                          </Badge>
                                        </td>
                                        <td style={{ ...S.td, fontSize: 12 }}>{mov.quantite}</td>
                                        <td style={{ ...S.td, fontSize: 12 }}>
                                          {new Date(mov.createdAt || mov.dateMouvement).toLocaleString('fr-FR')}
                                        </td>
                                        <td style={{ ...S.td, fontSize: 12 }}>{loc}</td>
                                        <td style={{ ...S.td, fontSize: 12 }}>{mov.notes || '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de réservation avec filtres */}
      {!!showTransferModal &&

        <StockTransferModal
          show={showTransferModal}
          onHide={() => setShowTransferModal(false)}
          intervention={intervention}
          onConfirm={() => fetchMovements()}
        />
      }
    </div>
  );
};

export default MaterialsTab;